import React, { useState, useEffect, useMemo, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { UpdateDispatchContext } from "components/SummonerProfiles/SummonerProfileUpdateReducer";
import { getSummonerProfileContext } from "components/SummonerProfiles/SummonerProfileContext";
import classnames from "classnames";
import MediaQuery from "components/MediaQuery";
import PlayedChampionsFilters from "./PlayedChampionsFilters";
import {
  getKdaColor,
  getKdaColorBestChampsClassName,
  calculateKDA,
  getQueueType,
  getWinRateBestChampsColorClassName,
} from "lib/general-helper.js";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { usePlayerStatistics } from "api/summonerProfileApi";
import { formatSummonerChampionStats } from "api/data_parser/summoner_champion_stats";
import { QUEUE_TYPE_OPTIONS, QUEUE_TYPE_MAP, RANKED_QUEUE_TYPE_MAP } from "./played-champions-filters";
import { defaultOverviewFilters } from "components/SummonerProfiles/views/ProfileOverview/overview-filters";
import LPGainArrows from "components/SummonerProfiles/views/ProfileOverview/LPBlock/LPGainArrows";
import { INTERNAL_CURRENT_SEASON, seasonMap } from "@ugg/shared/constants/seasons";

const EmptyBlock = (props) => {
  const { seasonId, filters } = props;
  const queueTypeLabel =
    filters.queueType === "all_ranked"
      ? "Ranked"
      : (QUEUE_TYPE_OPTIONS.find((option) => option.value === filters.queueType) || {}).label;

  const realSeason = `${seasonMap[INTERNAL_CURRENT_SEASON]?.season}-${seasonMap[INTERNAL_CURRENT_SEASON]?.split}`;

  return <div className="played-champions_empty">{`No S${realSeason} ${queueTypeLabel} games played.`}</div>;
};

const ChampionPerformance = (props) => {
  const { getChampionName, getNormalizedChampionName, getChampionImgFromSprite } = getRiotAssetsContext();
  const location = useLocation();
  const { data, region, summonerName } = props;

  const { assists, championId, cs, deaths, kills, totalMatches, wins, lpAvg } = data;

  const kdaRatio = calculateKDA(kills || 0, deaths || 0, assists || 0);
  const kdaColorClassName = getKdaColorBestChampsClassName(kdaRatio);
  const avgKills = Math.round(((kills || 0) / totalMatches) * 10) / 10;
  const avgDeaths = Math.round(((deaths || 0) / totalMatches) * 10) / 10;
  const avgAssists = Math.round(((assists || 0) / totalMatches) * 10) / 10;
  const winRate = Math.round(((wins || 0) / totalMatches) * 100);

  const championNameId = getNormalizedChampionName(championId);

  return (
    <div className="champion-performance">
      <div className="champion-face">{getChampionImgFromSprite(championId, { size: 38 })}</div>
      <div className="champion-stats">
        <div className={"champion-stats_col champion-stats_col-1 " + (props.isRanked ? "" : "champion-col-center")}>
          <div className={"champion-name " + (props.isRanked ? "" : "champion-name-center")}>{getChampionName(championId)}</div>
          {props.isRanked && (
            <div className="lp">
              <LPGainArrows lpAvg={lpAvg} />
            </div>
          )}
        </div>
        <div className="champion-stats_col champion-stats_col-2">
          <div className={classnames("kda-ratio", kdaColorClassName)}>
            <span>{kdaRatio}</span>
            <span> KDA</span>
          </div>
          <div className="kda-split">
            <span>{avgKills}</span>
            <span className="slash"> / </span>
            <span className="avg-deaths">{avgDeaths}</span>
            <span className="slash"> / </span>
            <span>{avgAssists}</span>
          </div>
        </div>
        <div className="champion-stats_col champion-stats_col-3">
          <div className="win-rate">
            <div className={getWinRateBestChampsColorClassName(winRate)}>{`${winRate}%`}</div>
          </div>
          <div className="total-games">{totalMatches} games</div>
        </div>
      </div>
    </div>
  );
};

const defaultFilters = {
  queueType: defaultOverviewFilters.playerStats.queueType,
};
const PlayedChampionsContainer = (props) => {
  const dispatch = useContext(UpdateDispatchContext);
  const context = getSummonerProfileContext();
  const { summonerName, region } = context;
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [filters, setFilters] = useState(defaultFilters);

  const { data, loading, error, refetch, variables } = usePlayerStatistics(
    summonerName,
    region,
    { queueType: QUEUE_TYPE_MAP[filters.queueType] },
    { fetchPolicy: !filtersChanged ? "cache-first" : "network-only" },
  );

  useEffect(() => {
    setFilters(defaultFilters);
  }, [summonerName, region]);

  useEffect(() => {
    dispatch({
      type: "addUpdates",
      payload: { playedChampions: refetch },
    });
  }, [JSON.stringify(variables)]);

  const { fetchPlayerStatistics } = data || {};
  const formattedData = useMemo(() => {
    const { fetchPlayerStatistics } = data || {};
    const champions = (fetchPlayerStatistics || []).reduce((acc, curr) => {
      return acc.concat(curr.basicChampionPerformances);
    }, []);
    return formatSummonerChampionStats(champions)
      .sort((a, b) => {
        const matchDiff = a.played.totalMatches - b.played.totalMatches;
        if (matchDiff > 0) {
          return -1;
        } else if (matchDiff < 0) {
          return 1;
        } else {
          const winRateDiff = a.winRate - b.winRate;
          if (winRateDiff > 0) {
            return -1;
          } else if (winRateDiff < 0) {
            return 1;
          }
          return 0;
        }
      })
      .slice(0, 7);
  }, [data]);

  let content = null;
  if (loading) {
    content = (
      <div className="played-champions_loading">
        <div className="spinthatshit-loader">
          <div className="spinner"></div>
        </div>
      </div>
    );
  } else if (formattedData.length > 0) {
    let rankedFilter = Object.keys(RANKED_QUEUE_TYPE_MAP).includes(filters.queueType);
    content = (
      <div className="champion-list">
        {formattedData.map((champion, index) => (
          <ChampionPerformance
            key={champion.championId}
            data={champion}
            region={region}
            summonerName={summonerName}
            isRanked={rankedFilter}
          />
        ))}
        <MediaQuery min="DESKTOP_MEDIUM" max="DESKTOP_LARGE">
          <Link
            style={{ display: formattedData.length < 7 ? "none" : "" }}
            to={`/lol/profile/${region}/${summonerName}/champion-stats`}
            className="show-more-btn"
          >
            See More Champions &#38; Seasons
          </Link>
        </MediaQuery>
        <MediaQuery min="MOBILE_LARGE" max="DESKTOP_SMALL">
          <Link
            style={{ display: formattedData.length < 3 ? "none" : "" }}
            to={`/lol/profile/${region}/${summonerName}/champion-stats`}
            className="show-more-btn"
          >
            See More Champions &#38; Seasons
          </Link>
        </MediaQuery>
        <MediaQuery min="MOBILE_SMALL" max="MOBILE_MEDIUM">
          <Link
            style={{ display: formattedData.length < 7 ? "none" : "" }}
            to={`/lol/profile/${region}/${summonerName}/champion-stats`}
            className="show-more-btn"
          >
            See More Champions &#38; Seasons
          </Link>
        </MediaQuery>
      </div>
    );
  } else {
    content = <EmptyBlock seasonId={variables.seasonId} filters={filters} />;
  }

  return (
    <div className="played-champions">
      <div className="played-champions_header">
        <div className="title">
          <div className="bluebar" />
          <span>Champion Stats</span>
        </div>
        <PlayedChampionsFilters filters={filters} setFilters={setFilters} setFiltersChanged={setFiltersChanged} />
      </div>
      {content}
    </div>
  );
};

export default PlayedChampionsContainer;
